<script setup>
import { computed } from 'vue';
import GoalProgressBar from './GoalProgressBar.vue';

const props = defineProps({
  agent: { type: Object, required: true },
  isAdmin: { type: Boolean, default: false },
});

const emit = defineEmits(['set-goal', 'view-sales']);

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const todaySales = computed(() => {
  if (!props.agent.daily_sales?.length) return { count: 0, amount: 0 };
  const today = new Date().toISOString().slice(0, 10);
  return props.agent.daily_sales.find(d => d.date === today) ?? { count: 0, amount: 0 };
});

const topType = computed(() => props.agent.sale_type_breakdown?.[0]?.type ?? null);

const agentMaxTypeTotal = computed(() =>
  (props.agent.sale_type_breakdown?.[0]?.total) ?? 0
);

const agentTypePct = total =>
  agentMaxTypeTotal.value > 0 ? Math.round((total / agentMaxTypeTotal.value) * 100) : 0;
</script>

<template>
  <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-4 flex flex-col gap-3">
    <!-- Header -->
    <div class="flex items-start justify-between gap-2">
      <div class="min-w-0">
        <span class="font-medium text-sm text-n-slate-12 leading-tight block">
          {{ agent.agent_name }}
        </span>
        <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span class="text-xs text-n-slate-10">
            {{ $t('SALES.CARD.TODAY') }}: {{ todaySales.count }} vendas
          </span>
          <span
            v-if="topType"
            class="text-[10px] px-1.5 py-0.5 rounded-full bg-n-alpha-3 border border-n-weak text-n-slate-10 flex items-center gap-1"
          >
            <span class="i-lucide-tag text-[9px]" />
            {{ topType }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-1 shrink-0 mt-0.5">
        <button
          class="p-1 rounded text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          :title="$t('SALES.CARD.VIEW_SALES')"
          @click="emit('view-sales', agent)"
        >
          <span class="i-lucide-list text-base" />
        </button>
        <button
          v-if="isAdmin"
          class="p-1 rounded text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          :title="$t('SALES.GOAL.SET_GOAL')"
          @click="emit('set-goal', { agentId: agent.agent_id, agentName: agent.agent_name })"
        >
          <span class="i-lucide-target text-base" />
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 gap-2 text-center">
      <div class="bg-n-alpha-black2 rounded-lg p-2">
        <p class="text-lg font-semibold text-n-slate-12">{{ agent.total_with_sale }}</p>
        <p class="text-[11px] text-n-slate-10">{{ $t('SALES.CARD.TOTAL_SALES') }}</p>
      </div>
      <div class="bg-n-alpha-black2 rounded-lg p-2">
        <p class="text-lg font-semibold text-n-slate-12">{{ brl(agent.total_amount) }}</p>
        <p class="text-[11px] text-n-slate-10">{{ $t('SALES.CARD.TOTAL_AMOUNT') }}</p>
      </div>
    </div>

    <!-- Tipos de venda -->
    <div v-if="agent.sale_type_breakdown?.length" class="border-t border-n-weak pt-2 flex flex-col gap-1.5">
      <p class="text-[10px] font-medium text-n-slate-9 uppercase tracking-wide">
        {{ $t('SALES.SALE_TYPE.LABEL') }}
      </p>
      <div
        v-for="item in agent.sale_type_breakdown.slice(0, 3)"
        :key="item.type"
        class="flex flex-col gap-0.5"
      >
        <div class="flex justify-between text-[10px] text-n-slate-10">
          <span class="truncate max-w-[60%]">{{ item.type }}</span>
          <span class="shrink-0">{{ item.count }}x · {{ brl(item.total) }}</span>
        </div>
        <div class="h-1 rounded-full bg-n-weak overflow-hidden">
          <div
            class="h-full bg-n-brand rounded-full"
            :style="{ width: agentTypePct(item.total) + '%' }"
          />
        </div>
      </div>
    </div>

    <!-- Meta -->
    <div class="border-t border-n-weak pt-3">
      <p class="text-[11px] font-medium text-n-slate-9 mb-2 uppercase tracking-wide">
        {{ $t('SALES.GOAL.MY_GOAL') }}
      </p>
      <GoalProgressBar :goal="agent.goal" />
    </div>
  </div>
</template>
